import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/validators';
import api from '@/lib/api';
import { Plus, Pencil, Trash2, Power, PowerOff, RefreshCw, Paperclip } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import PhoneAttachmentCategories from './Admin/PhoneAttachmentCategories';
import { useIsMobile } from '@/hooks/useIsMobile';

const ALL_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'designer', label: 'Designer' },
  // { value: 'head_designer', label: 'Head Designer' },  // post open — restore with the role
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'machine', label: 'Machine Operator' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'accountant', label: 'Accountant' },
];

const DEFAULT_COLORS = [
  '#3b82f6', '#8b5cf6', '#6366f1', '#14b8a6', '#f59e0b',
  '#10b981', '#ef4444', '#6b7280', '#ec4899', '#f97316',
];

export default function AttachmentCategoriesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color_hex: '#6366f1',
    allowed_roles: [],
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  
  // Delete state
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/attachment-categories', { params: { active_only: false } });
      setCategories(res.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Open dialog for create/edit
  const openDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        color_hex: category.color_hex,
        allowed_roles: category.allowed_roles || [],
        is_active: category.is_active,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        color_hex: DEFAULT_COLORS[categories.length % DEFAULT_COLORS.length],
        allowed_roles: [],
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  // Handle save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await api.patch(`/admin/attachment-categories/${editingCategory.id}`, formData);
        toast.success('Category updated');
      } else {
        await api.post('/admin/attachment-categories', formData);
        toast.success('Category created');
      }
      setDialogOpen(false);
      loadCategories();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save category'));
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteCategory) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/attachment-categories/${deleteCategory.id}`);
      toast.success('Category deleted');
      setDeleteCategory(null);
      loadCategories();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete category'));
    } finally {
      setDeleting(false);
    }
  };

  // Toggle role in allowed_roles
  const toggleRole = (role) => {
    setFormData((prev) => ({
      ...prev,
      allowed_roles: prev.allowed_roles.includes(role)
        ? prev.allowed_roles.filter((r) => r !== role)
        : [...prev.allowed_roles, role],
    }));
  };

  // Toggle active status
  const toggleActive = async (category) => {
    try {
      await api.patch(`/admin/attachment-categories/${category.id}`, {
        is_active: !category.is_active,
      });
      loadCategories();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to change status'));
    }
  };

  if (user?.role !== 'admin') {
    return null;
  }

  const columns = [
    {
      key: 'name',
      header: 'Category',
      width: 'minmax(12rem,1.4fr)',
      render: (c) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-3 w-3 shrink-0 rounded-full border border-slate-200"
            style={{ backgroundColor: c.color_hex }} />
          <span className={`truncate font-semibold ${c.is_active ? 'text-slate-900' : 'text-slate-400'}`}>
            {c.name}
          </span>
        </span>
      ),
    },
    {
      key: 'allowed_roles',
      header: 'Who can upload',
      width: 'minmax(14rem,1.8fr)',
      render: (c) => c.allowed_roles?.length ? (
        <span className="flex flex-wrap gap-1">
          {c.allowed_roles.map(r => (
            <span key={r} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {ALL_ROLES.find(role => role.value === r)?.label || r}
            </span>
          ))}
        </span>
      ) : <span className="text-slate-400">Everyone</span>,
    },
    {
      key: 'attachment_count',
      header: 'Files',
      align: 'right',
      width: '6rem',
      render: (c) => (
        <span className={c.attachment_count ? 'font-medium text-slate-700' : 'text-slate-400'}>
          {c.attachment_count ?? '—'}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '6rem',
      render: (c) => (
        <span className={`text-[12px] font-medium ${c.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
          {c.is_active ? 'active' : 'inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '6rem',
      align: 'right',
      render: (c) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Pencil} label="Edit category" iconOnly onClick={() => openDialog(c)} />
          <CellButton
            icon={c.is_active ? PowerOff : Power}
            label={c.is_active ? 'Deactivate' : 'Activate'}
            iconOnly
            tone={c.is_active ? 'danger' : 'good'}
            onClick={() => toggleActive(c)}
          />
          <CellButton icon={Trash2} label="Delete category" iconOnly tone="danger"
            onClick={() => setDeleteCategory(c)} />
        </span>
      ),
    },
  ];

  const panels = (
    <>
      <SidePane
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingCategory ? `Edit ${editingCategory.name}` : 'Add category'}
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {saving ? 'Saving…' : editingCategory ? 'Save changes' : 'Create category'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Name *</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={formData.name}
              placeholder="e.g. Customer Reference"
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} />
          </label>

          <div>
            <span className="text-[11px] font-medium text-slate-600">Colour</span>
            <div className="mt-0.5 flex items-center gap-2">
              <input
                type="color"
                value={formData.color_hex}
                onChange={(e) => setFormData(p => ({ ...p, color_hex: e.target.value }))}
                aria-label="Category colour"
                className="h-8 w-8 cursor-pointer rounded border border-slate-200"
              />
              <div className="flex flex-wrap gap-1">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Use ${color}`}
                    className={`h-5 w-5 rounded-full border-2 transition-all ${
                      formData.color_hex === color ? 'scale-110 border-slate-900' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData(p => ({ ...p, color_hex: color }))}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <span className="text-[11px] font-medium text-slate-600">
              Who can upload
              <span className="ml-1 text-slate-400">
                {formData.allowed_roles.length === 0 ? '(everyone)' : `(${formData.allowed_roles.length})`}
              </span>
            </span>
            <div className="mt-0.5 grid grid-cols-2 gap-1">
              {ALL_ROLES.map((role) => (
                <label key={role.value}
                  className="flex cursor-pointer items-center gap-1.5 rounded border border-slate-200 px-1.5 py-1 hover:bg-slate-50">
                  <Checkbox
                    className="h-3.5 w-3.5"
                    checked={formData.allowed_roles.includes(role.value)}
                    onCheckedChange={() => toggleRole(role.value)}
                  />
                  <span className="truncate text-[12px]">{role.label}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5">
            <span className="text-[12px] font-medium text-slate-700">Active</span>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(p => ({ ...p, is_active: checked }))}
            />
          </label>
        </div>
      </SidePane>

      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteCategory?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Files already filed under it keep their category name, but nothing new can be uploaded to it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700">
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (isMobile) {
    return (
      <>
        <PhoneAttachmentCategories
          categories={categories}
          loading={loading}
          roleLabel={(r) => ALL_ROLES.find(role => role.value === r)?.label || r}
          onBack={() => navigate('/admin-settings')}
          onAdd={() => openDialog()}
          onRefresh={loadCategories}
          onEdit={openDialog}
          onToggleActive={toggleActive}
          onDelete={setDeleteCategory}
        />
        {panels}
      </>
    );
  }

  return (
    <Screen data-testid="attachment-categories-page">
      <CommandBar
        title="Attachment Categories"
        subtitle="What files can be filed under, and who may upload them"
        onBack={() => navigate('/admin-settings')}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: loadCategories },
          { key: 'add', label: 'Add Category', icon: Plus, tone: 'accent', onClick: () => openDialog() },
        ]}
      />

      <StatStrip
        stats={[
          { key: null, label: 'categories', value: categories.length, tone: 'blue' },
          { key: null, label: 'active', value: categories.filter(c => c.is_active).length, tone: 'good' },
          {
            key: null,
            label: 'open to everyone',
            value: categories.filter(c => !c.allowed_roles?.length).length,
            tone: 'warn',
          },
        ]}
      />

      <Pane title="Categories" aside={`${categories.length}`} scroll={false} bodyClassName="flex flex-col">
        <DataGrid
          data-testid="attachment-categories-table"
          columns={columns}
          rows={categories}
          loading={loading}
          getRowId={(c) => c.id}
          onRowClick={(c) => openDialog(c)}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Paperclip className="h-8 w-8 text-slate-300" />
              No categories yet — add one so files have somewhere to go.
            </span>
          }
        />
      </Pane>

      {panels}
    </Screen>
  );
}
